import { translateLabel } from "@/i18n/messages";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRightIcon, ArrowSquareOutIcon, CalendarBlankIcon, MapPinIcon, XIcon } from "@phosphor-icons/react";
import { getRideEventSeries } from "@/lib/eventsApi";
import { ShareMenu } from "@/components/ShareMenu";
import { eventSharePayload } from "@/lib/share";

function dateLabel(startsAt, endsAt, timeZone) {
  const format = (value) => new Intl.DateTimeFormat("en-GB", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    timeZone: timeZone || "UTC",
  }).format(new Date(value));
  return `${format(startsAt)} – ${format(endsAt)}`;
}

/** Render next to the Discover map on desktop, or inside a mobile sheet. */
export function EventDetails({ event, onClose, onSelectEvent }) {
  const [series, setSeries] = useState(null);
  const [seriesError, setSeriesError] = useState(false);

  useEffect(() => {
    setSeries(null);
    setSeriesError(false);
    if (!event?.seriesId) return undefined;
    const controller = new AbortController();
    getRideEventSeries(event.seriesId, { signal: controller.signal })
      .then((result) => { if (!controller.signal.aborted) setSeries(result); })
      .catch(() => { if (!controller.signal.aborted) setSeriesError(true); });
    return () => controller.abort();
  }, [event?.seriesId]);

  if (!event) return null;
  const bookUrl = event.registrationUrl;
  const verifiedLink = typeof bookUrl === "string" && /^https:\/\//i.test(bookUrl);
  const stops = series?.id === event.seriesId ? series.events : [];

  return (
    <section aria-label={translateLabel("Event details")} className="flex h-full min-h-0 flex-col overflow-y-auto bg-[#111214] text-white">
      <div className="sticky top-0 z-10 flex items-center justify-between border-b border-white/10 bg-[#111214] px-4 py-3">
        <span className="text-xs font-semibold uppercase tracking-widest text-lime-400">{translateLabel("Event details")}</span>
        <button type="button" onClick={onClose} aria-label={translateLabel("Close event details")} className="rounded-lg p-2 hover:bg-white/10"><XIcon size={20} /></button>
      </div>
      {event.imageUrl && <img src={event.imageUrl} alt="" className="max-h-52 w-full object-cover" loading="lazy" />}
      <div className="space-y-5 p-5">
        <div>
          <span className="text-xs uppercase tracking-wide text-lime-400">{event.category}</span>
          <h2 className="mt-2 text-xl font-bold leading-tight">{event.title}</h2>
          <p className="mt-2 text-sm text-zinc-400">Organized by {event.organizer}</p>
        </div>
        <div className="space-y-3 text-sm text-zinc-200">
          <div className="flex items-start gap-2"><CalendarBlankIcon className="mt-0.5 shrink-0 text-lime-400" size={18} /><span>{dateLabel(event.startsAt, event.endsAt, event.timeZone)}</span></div>
          <div className="flex items-start gap-2"><MapPinIcon className="mt-0.5 shrink-0 text-lime-400" size={18} /><span>{event.venueName}, {event.city}</span></div>
        </div>
        {event.description && <p className="whitespace-pre-line text-sm leading-relaxed text-zinc-300">{event.description}</p>}
        <div className="flex flex-wrap gap-2">
          <ShareMenu payload={eventSharePayload(event)} className="flex-1" />
          <Link to={`/events?event=${encodeURIComponent(event.id)}`} className="flex flex-1 items-center justify-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-white hover:bg-white/10">{translateLabel("Open event")} <ArrowRightIcon size={18} /></Link>
          {verifiedLink && <a href={bookUrl} target="_blank" rel="noopener noreferrer" className="flex flex-1 items-center justify-center gap-2 rounded-full bg-lime-400 px-4 py-3 text-sm font-semibold text-black hover:bg-lime-300">{translateLabel("Official registration")} <ArrowSquareOutIcon size={18} /></a>}
        </div>
        <a href={event.sourceUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-zinc-300 underline underline-offset-4 hover:text-white">{translateLabel("Event source")} <ArrowSquareOutIcon size={15} /></a>
        {event.seriesId && <div className="border-t border-white/10 pt-5">
          <h3 className="text-sm font-semibold">{translateLabel("Other tour stops")}</h3>
          {stops.length > 0 && <div className="mt-3 space-y-2">{stops.filter((stop) => stop.id !== event.id).map((stop) => (
            <button key={stop.id} type="button" onClick={() => onSelectEvent?.(stop)} className="w-full rounded-lg border border-white/10 p-3 text-left hover:border-lime-400/70">
              <span className="block text-sm font-semibold">{stop.venueName}, {stop.city}</span>
              <span className="mt-1 block text-xs text-zinc-400">{dateLabel(stop.startsAt, stop.endsAt, stop.timeZone)}</span>
            </button>
          ))}</div>}
          {!series && !seriesError && <p className="mt-3 text-xs text-zinc-400">{translateLabel("Loading tour stops…")}</p>}
          {seriesError && <p className="mt-3 text-xs text-zinc-400">{translateLabel("Other stops are currently unavailable.")}</p>}
        </div>}
      </div>
    </section>
  );
}
